import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from '@/i18n/LanguageContext';
import LanguageRedirect from '@/components/LanguageRedirect';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import Terms from '@/pages/Terms';
import Questionnaire from '@/pages/Questionnaire';
import Dashboard from '@/pages/Dashboard';
import Discover from '@/pages/Discover';
import Matches from '@/pages/Matches';
import Chat from '@/pages/Chat';
import Payment from '@/pages/Payment';
import Admin from '@/pages/Admin';
import FAQ from '@/pages/FAQ';
import HowItWorks from '@/pages/HowItWorks';
import About from '@/pages/About';
import Blog from '@/pages/Blog';
import BlogArticle from '@/pages/BlogArticle';
import Testimonials from '@/pages/Testimonials';
import SubmitTestimonial from '@/pages/SubmitTestimonial';
import '@/App.css';

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  const currentLang = window.location.pathname.match(/^\/(fr|en|es|de)/)?.[1] || 'fr';
  return token ? children : <Navigate to={`/${currentLang}/auth`} />;
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <div className="App">
          <Routes>
            {/* Root redirect */}
            <Route path="/" element={<LanguageRedirect />} />
            
            {/* French routes (default) */}
            <Route path="/fr" element={<Landing />} />
            <Route path="/fr/auth" element={<Auth />} />
            <Route path="/fr/terms" element={<Terms />} />
            <Route path="/fr/payment" element={<Payment />} />
            <Route path="/fr/payment/success" element={<Payment />} />
            <Route path="/fr/payment/cancel" element={<Payment />} />
            <Route path="/fr/admin" element={<Admin />} />
            <Route path="/fr/faq" element={<FAQ />} />
            <Route path="/fr/how-it-works" element={<HowItWorks />} />
            <Route path="/fr/about" element={<About />} />
            <Route path="/fr/blog" element={<Blog />} />
            <Route path="/fr/blog/:slug" element={<BlogArticle />} />
            <Route path="/fr/testimonials" element={<Testimonials />} />
            <Route path="/fr/submit-testimonial" element={<SubmitTestimonial />} />
            <Route path="/fr/questionnaire" element={<PrivateRoute><Questionnaire /></PrivateRoute>} />
            <Route path="/fr/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/fr/discover" element={<PrivateRoute><Discover /></PrivateRoute>} />
            <Route path="/fr/matches" element={<PrivateRoute><Matches /></PrivateRoute>} />
            <Route path="/fr/chat/:matchId" element={<PrivateRoute><Chat /></PrivateRoute>} />

            {/* English routes */}
            <Route path="/en" element={<Landing />} />
            <Route path="/en/auth" element={<Auth />} />
            <Route path="/en/terms" element={<Terms />} />
            <Route path="/en/payment" element={<Payment />} />
            <Route path="/en/payment/success" element={<Payment />} />
            <Route path="/en/payment/cancel" element={<Payment />} />
            <Route path="/en/admin" element={<Admin />} />
            <Route path="/en/faq" element={<FAQ />} />
            <Route path="/en/how-it-works" element={<HowItWorks />} />
            <Route path="/en/about" element={<About />} />
            <Route path="/en/blog" element={<Blog />} />
            <Route path="/en/blog/:slug" element={<BlogArticle />} />
            <Route path="/en/testimonials" element={<Testimonials />} />
            <Route path="/en/submit-testimonial" element={<SubmitTestimonial />} />
            <Route path="/en/questionnaire" element={<PrivateRoute><Questionnaire /></PrivateRoute>} />
            <Route path="/en/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/en/discover" element={<PrivateRoute><Discover /></PrivateRoute>} />
            <Route path="/en/matches" element={<PrivateRoute><Matches /></PrivateRoute>} />
            <Route path="/en/chat/:matchId" element={<PrivateRoute><Chat /></PrivateRoute>} />

            {/* Spanish routes */}
            <Route path="/es" element={<Landing />} />
            <Route path="/es/auth" element={<Auth />} />
            <Route path="/es/terms" element={<Terms />} />
            <Route path="/es/payment" element={<Payment />} />
            <Route path="/es/payment/success" element={<Payment />} />
            <Route path="/es/payment/cancel" element={<Payment />} />
            <Route path="/es/admin" element={<Admin />} />
            <Route path="/es/faq" element={<FAQ />} />
            <Route path="/es/how-it-works" element={<HowItWorks />} />
            <Route path="/es/about" element={<About />} />
            <Route path="/es/blog" element={<Blog />} />
            <Route path="/es/blog/:slug" element={<BlogArticle />} />
            <Route path="/es/testimonials" element={<Testimonials />} />
            <Route path="/es/submit-testimonial" element={<SubmitTestimonial />} />
            <Route path="/es/questionnaire" element={<PrivateRoute><Questionnaire /></PrivateRoute>} />
            <Route path="/es/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/es/discover" element={<PrivateRoute><Discover /></PrivateRoute>} />
            <Route path="/es/matches" element={<PrivateRoute><Matches /></PrivateRoute>} />
            <Route path="/es/chat/:matchId" element={<PrivateRoute><Chat /></PrivateRoute>} />

            {/* German routes */}
            <Route path="/de" element={<Landing />} />
            <Route path="/de/auth" element={<Auth />} />
            <Route path="/de/terms" element={<Terms />} />
            <Route path="/de/payment" element={<Payment />} />
            <Route path="/de/payment/success" element={<Payment />} />
            <Route path="/de/payment/cancel" element={<Payment />} />
            <Route path="/de/admin" element={<Admin />} />
            <Route path="/de/faq" element={<FAQ />} />
            <Route path="/de/how-it-works" element={<HowItWorks />} />
            <Route path="/de/about" element={<About />} />
            <Route path="/de/blog" element={<Blog />} />
            <Route path="/de/blog/:slug" element={<BlogArticle />} />
            <Route path="/de/testimonials" element={<Testimonials />} />
            <Route path="/de/submit-testimonial" element={<SubmitTestimonial />} />
            <Route path="/de/questionnaire" element={<PrivateRoute><Questionnaire /></PrivateRoute>} />
            <Route path="/de/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/de/discover" element={<PrivateRoute><Discover /></PrivateRoute>} />
            <Route path="/de/matches" element={<PrivateRoute><Matches /></PrivateRoute>} />
            <Route path="/de/chat/:matchId" element={<PrivateRoute><Chat /></PrivateRoute>} />

            {/* Fallback for old routes without language prefix - redirect to French */}
            <Route path="/auth" element={<Navigate to="/fr/auth" replace />} />
            <Route path="/terms" element={<Navigate to="/fr/terms" replace />} />
            <Route path="/payment" element={<Navigate to="/fr/payment" replace />} />
            <Route path="/admin" element={<Navigate to="/fr/admin" replace />} />
            <Route path="/faq" element={<Navigate to="/fr/faq" replace />} />
            <Route path="/how-it-works" element={<Navigate to="/fr/how-it-works" replace />} />
            <Route path="/about" element={<Navigate to="/fr/about" replace />} />
            <Route path="/blog" element={<Navigate to="/fr/blog" replace />} />
            <Route path="/blog/:slug" element={<Navigate to="/fr/blog/:slug" replace />} />
            <Route path="/testimonials" element={<Navigate to="/fr/testimonials" replace />} />
            <Route path="/submit-testimonial" element={<Navigate to="/fr/submit-testimonial" replace />} />
            <Route path="/questionnaire" element={<Navigate to="/fr/questionnaire" replace />} />
            <Route path="/dashboard" element={<Navigate to="/fr/dashboard" replace />} />
            <Route path="/discover" element={<Navigate to="/fr/discover" replace />} />
            <Route path="/matches" element={<Navigate to="/fr/matches" replace />} />
          </Routes>
          <Toaster />
        </div>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
